import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, Loader2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { API, getAuthHeaders } from '../../utils/adminAuth';

export default function CollectionModal({ collection, onClose, onSave }) {
  const isEdit = !!collection;
  const [form, setForm] = useState({
    title: collection?.title || '',
    subtitle: collection?.subtitle || 'Collection',
    image_url: collection?.image_url || '',
    link_url: collection?.link_url || '/shop',
    sort_order: collection?.sort_order ?? 0,
    is_active: collection?.is_active ?? true,
    members_only: collection?.members_only ?? false,
    members_teaser: collection?.members_teaser || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const LINK_PRESETS = [
    { label: 'Men Collection', value: '/shop?gender=Men' },
    { label: 'Women Collection', value: '/shop?gender=Women' },
    { label: 'Unisex Collection', value: '/shop?gender=Unisex' },
    { label: 'JS Signatures', value: '/shop?signature=true' },
    { label: 'All Products', value: '/shop' },
  ];

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/product-image`, fd, { headers: getAuthHeaders() });
      const imageUrl = `${API}/files/${res.data.storage_path}`;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file?.type?.startsWith('image/')) handleImageUpload(file);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/collections/${collection.id}`, form, { headers: getAuthHeaders() });
        toast.success('Collection updated');
      } else {
        await axios.post(`${API}/admin/collections`, form, { headers: getAuthHeaders() });
        toast.success('Collection created');
      }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="collection-modal">
      <div className="bg-[#141414] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {isEdit ? 'Edit Collection' : 'New Collection'}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <input type="text" placeholder="Collection title (e.g. For Him, JS Signatures)" value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
            data-testid="collection-title-input" />

          <input type="text" placeholder="Subtitle (e.g. Collection, Featured)" value={form.subtitle}
            onChange={e => setForm({ ...form, subtitle: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
            data-testid="collection-subtitle-input" />

          <div>
            <p className="text-white/40 text-xs uppercase tracking-wider mb-2 font-sans">Image</p>
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => document.getElementById('col-img-input')?.click()}
              className="border border-dashed border-white/20 hover:border-[#D4AF37]/50 p-4 text-center cursor-pointer transition-colors"
              data-testid="collection-image-dropzone"
            >
              {form.image_url ? (
                <img src={form.image_url} alt="Preview" className="w-full h-32 object-cover mb-2" />
              ) : uploading ? (
                <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin mx-auto" />
              ) : (
                <div>
                  <Upload className="w-6 h-6 text-white/20 mx-auto mb-1" />
                  <p className="text-white/30 text-xs font-sans">Drop image or click to upload</p>
                </div>
              )}
              <input id="col-img-input" type="file" accept="image/*" className="hidden"
                onChange={e => handleImageUpload(e.target.files?.[0])} />
            </div>
            <input type="text" placeholder="Or paste image URL" value={form.image_url}
              onChange={e => setForm({ ...form, image_url: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-xs mt-2 focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
              data-testid="collection-image-url-input" />
          </div>

          <div>
            <p className="text-white/40 text-xs uppercase tracking-wider mb-2 font-sans">Links To</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {LINK_PRESETS.map(preset => (
                <button key={preset.value} type="button"
                  onClick={() => setForm({ ...form, link_url: preset.value })}
                  className={`text-xs px-2.5 py-1 border transition-colors font-sans ${form.link_url === preset.value ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/20 text-white/40 hover:border-white/40'}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <input type="text" placeholder="Custom link (e.g. /product/abc-123)" value={form.link_url}
              onChange={e => setForm({ ...form, link_url: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
              data-testid="collection-link-input" />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-1 font-sans">Sort Order</p>
              <input type="number" value={form.sort_order}
                onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
                data-testid="collection-sort-input" />
            </div>
            <div className="flex items-center gap-2 pt-4">
              <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="collection-active-toggle" />
              <span className="text-white/50 text-xs font-sans">{form.is_active ? 'Visible' : 'Hidden'}</span>
            </div>
          </div>

          <div className="border-t border-white/5 pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-sans font-medium">Members-only collection</p>
                <p className="text-white/40 text-[11px] font-sans mt-0.5">Locked until customer's first purchase. Visible-but-blurred for non-members.</p>
              </div>
              <Switch checked={form.members_only} onCheckedChange={v => setForm({ ...form, members_only: v })} data-testid="collection-members-only-toggle" />
            </div>
            {form.members_only && (
              <div>
                <p className="text-white/40 text-xs uppercase tracking-wider mb-1 font-sans">Teaser text (shown to non-members)</p>
                <input type="text" placeholder="e.g. Unlock our 2026 Holiday drop after your first order"
                  value={form.members_teaser}
                  onChange={e => setForm({ ...form, members_teaser: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
                  data-testid="collection-members-teaser-input" />
              </div>
            )}
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="w-full bg-[#D4AF37] text-black py-2.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 mt-5"
          data-testid="save-collection-btn">
          {saving ? 'Saving...' : isEdit ? 'Update Collection' : 'Create Collection'}
        </button>
      </div>
    </div>
  );
}
